import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Shield } from "lucide-react";

export default function PoliticaPrivacidade() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 px-6 py-4 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Voltar
        </button>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-emerald-400" />
          <span className="font-semibold text-white">Política de Privacidade</span>
        </div>
      </div>

      {/* Conteúdo */}
      <div className="max-w-3xl mx-auto px-6 py-12 space-y-8 text-slate-300 text-sm leading-relaxed">
        <div>
          <h1 className="text-2xl font-bold text-white mb-2">Política de Privacidade</h1>
          <p className="text-slate-500 text-xs">Última atualização: outubro de 2026</p>
        </div>

        <p>
          O <strong className="text-white">Monyxx</strong> respeita sua privacidade e está comprometido
          com a proteção dos seus dados pessoais, em conformidade com a
          <strong className="text-white"> Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018)</strong>.
          Esta política descreve quais dados coletamos, como os utilizamos e quais são seus direitos.
        </p>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">1. Dados que Coletamos</h2>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li><strong className="text-slate-200">Cadastro:</strong> nome, e-mail e senha (armazenada com hash seguro).</li>
            <li><strong className="text-slate-200">Login social:</strong> nome e e-mail fornecidos pelo Google via Firebase Authentication.</li>
            <li><strong className="text-slate-200">Dados financeiros:</strong> lançamentos de entradas, gastos fixos, gastos variáveis, cartões de crédito, investimentos e objetivos que você insere voluntariamente.</li>
            <li><strong className="text-slate-200">Áudio (voz):</strong> gravações de voz enviadas para transcrição. O áudio é processado em tempo real e não é armazenado permanentemente.</li>
            <li><strong className="text-slate-200">Pagamento:</strong> e-mail e identificador de transação fornecidos pelas plataformas de pagamento. Não armazenamos dados de cartão de crédito.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">2. Como Usamos seus Dados</h2>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Fornecer e personalizar as funcionalidades do aplicativo.</li>
            <li>Processar pagamentos e ativar seu plano de assinatura.</li>
            <li>Transcrever comandos de voz para criação de lançamentos financeiros.</li>
            <li>Enviar e-mails transacionais (confirmação de cadastro, ativação de plano).</li>
            <li>Garantir a segurança e integridade da conta.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">3. Compartilhamento de Dados</h2>
          <p>Não vendemos nem compartilhamos seus dados pessoais com terceiros para fins comerciais. Compartilhamos apenas com prestadores de serviço essenciais ao funcionamento do app:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 mt-2">
            <li><strong className="text-slate-200">Google Firebase:</strong> autenticação via conta Google.</li>
            <li><strong className="text-slate-200">Groq:</strong> transcrição de áudio e processamento de linguagem natural para lançamentos por voz.</li>
            <li><strong className="text-slate-200">Mercado Pago / Hotmart:</strong> processamento de pagamentos.</li>
            <li><strong className="text-slate-200">MongoDB Atlas:</strong> armazenamento seguro dos dados na nuvem.</li>
            <li><strong className="text-slate-200">Render:</strong> infraestrutura de hospedagem do servidor.</li>
          </ul>
          <p className="mt-2">Todos esses parceiros possuem suas próprias políticas de privacidade e tratam os dados exclusivamente para as finalidades contratadas.</p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">4. Armazenamento e Segurança</h2>
          <ul className="list-disc list-inside space-y-1 pl-2">
            <li>Seus dados são armazenados em servidores com criptografia em trânsito (HTTPS/TLS).</li>
            <li>Senhas são armazenadas com hash bcrypt — nunca em texto simples.</li>
            <li>Sessões são protegidas por tokens JWT com validade limitada.</li>
            <li>Cookies de autenticação são HttpOnly, impedindo acesso via JavaScript.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">5. Retenção de Dados</h2>
          <p>
            Mantemos seus dados enquanto sua conta estiver ativa. Ao solicitar a exclusão da conta,
            seus dados pessoais e financeiros são removidos permanentemente em até 30 dias,
            exceto quando exigida retenção por obrigação legal.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">6. Seus Direitos (LGPD)</h2>
          <p>Conforme a LGPD, você tem direito a:</p>
          <ul className="list-disc list-inside space-y-1 pl-2 mt-2">
            <li>Confirmar a existência de tratamento dos seus dados.</li>
            <li>Acessar os dados que temos sobre você.</li>
            <li>Corrigir dados incompletos, inexatos ou desatualizados.</li>
            <li>Solicitar a anonimização, bloqueio ou eliminação de dados desnecessários.</li>
            <li>Solicitar a portabilidade dos seus dados.</li>
            <li>Revogar o consentimento a qualquer momento.</li>
          </ul>
          <p className="mt-2">
            Para exercer qualquer direito, entre em contato:
            <a href="mailto:suporte@monyxx.com.br" className="text-emerald-400 hover:text-emerald-300 ml-1">
              suporte@monyxx.com.br
            </a>
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">7. Cookies</h2>
          <p>
            Utilizamos cookies estritamente necessários para manter sua sessão autenticada.
            Não utilizamos cookies de rastreamento ou publicidade de terceiros.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">8. Menores de Idade</h2>
          <p>
            O Monyxx não é destinado a menores de 18 anos. Não coletamos conscientemente dados
            de crianças ou adolescentes.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">9. Alterações nesta Política</h2>
          <p>
            Podemos atualizar esta política periodicamente. Em caso de mudanças relevantes,
            notificaremos por e-mail ou aviso no aplicativo. O uso continuado após a notificação
            implica aceitação da nova versão.
          </p>
        </section>

        <section className="space-y-3">
          <h2 className="text-base font-semibold text-white">10. Contato</h2>
          <p>
            Dúvidas sobre esta política? Entre em contato com nosso time:
          </p>
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mt-2">
            <p className="text-white font-medium">Monyxx — Suporte</p>
            <p className="text-slate-400 mt-1">
              <a href="mailto:suporte@monyxx.com.br" className="text-emerald-400 hover:text-emerald-300">
                suporte@monyxx.com.br
              </a>
            </p>
          </div>
        </section>

        <div className="border-t border-slate-800 pt-6 text-center text-slate-500 text-xs">
          © {new Date().getFullYear()} Monyxx. Todos os direitos reservados.
        </div>
      </div>
    </div>
  );
}
